import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { usePayPalScriptReducer } from "@paypal/react-paypal-js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Standalone Google Pay button.
 * Requires:
 *  - PayPalScriptProvider loaded with `components: "buttons,applepay,googlepay"`
 *  - <script src="https://pay.google.com/gp/p/js/pay.js"> in index.html
 *
 * Visibility rules:
 *  - window.google.payments.api must be available
 *  - paypal.Googlepay().config() must return { isEligible: true }
 *  - PaymentsClient.isReadyToPay(...) must resolve with { result: true }
 */
export const GooglePayButton = ({ request, onPaid, onError }) => {
  const [{ isResolved }] = usePayPalScriptReducer();
  const [googlepayConfig, setGooglepayConfig] = useState(null);
  const [eligible, setEligible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isResolved || !request) return;
    const paypal = window.paypal;
    if (!paypal || !paypal.Googlepay) return;
    if (!window.google || !window.google.payments || !window.google.payments.api) return;

    // Google Pay for Web is NOT supported on iOS Safari (Apple restricts it).
    // Show Apple Pay on iOS instead.
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isIOSSafari = isIOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOSSafari) return;

    (async () => {
      try {
        const cfg = await paypal.Googlepay().config();
        if (!cfg || !cfg.isEligible) return;

        const client = new window.google.payments.api.PaymentsClient({
          environment: cfg.environment === "TEST" ? "TEST" : "PRODUCTION",
        });
        const readyReq = {
          apiVersion: cfg.apiVersion || 2,
          apiVersionMinor: cfg.apiVersionMinor || 0,
          allowedPaymentMethods: cfg.allowedPaymentMethods,
        };
        const ready = await client.isReadyToPay(readyReq);
        if (!ready || !ready.result) return;

        setGooglepayConfig({ ...cfg, client });
        setEligible(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[GooglePay] config check failed", e);
      }
    })();
  }, [isResolved, request]);

  useEffect(() => {
    if (!eligible || !googlepayConfig || !containerRef.current) return;
    const { client, allowedPaymentMethods } = googlepayConfig;
    // Clear any previous button
    containerRef.current.innerHTML = "";
    const btn = client.createButton({
      onClick: handleClick,
      buttonColor: "black",
      buttonType: "pay",
      buttonSizeMode: "fill",
      buttonRadius: 999,
      allowedPaymentMethods,
    });
    containerRef.current.appendChild(btn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, googlepayConfig]);

  const handleClick = async () => {
    if (!request || !googlepayConfig) return;
    const paypal = window.paypal;
    const cfg = googlepayConfig;
    try {
      // 1. Create PayPal order server-side
      const { data } = await axios.post(`${API}/paypal/create-order`, { request_id: request.id });
      const orderId = data.id;

      // 2. Build PaymentDataRequest from PayPal's Google Pay config
      const paymentDataRequest = {
        apiVersion: cfg.apiVersion || 2,
        apiVersionMinor: cfg.apiVersionMinor || 0,
        allowedPaymentMethods: cfg.allowedPaymentMethods,
        merchantInfo: cfg.merchantInfo,
        transactionInfo: {
          countryCode: cfg.countryCode || "IT",
          currencyCode: request.currency || "EUR",
          totalPriceStatus: "FINAL",
          totalPrice: request.amount.toFixed(2),
        },
        callbackIntents: ["PAYMENT_AUTHORIZATION"],
      };

      // 3. Show Google Pay sheet
      const paymentData = await cfg.client.loadPaymentData(paymentDataRequest);

      // 4. Confirm order with PayPal
      const confirm = await paypal.Googlepay().confirmOrder({
        orderId,
        paymentMethodData: paymentData.paymentMethodData,
      });

      if (confirm.status === "APPROVED") {
        // 5. Server-side capture — sole trigger for the tarot-question email
        await axios.post(`${API}/paypal/capture/${orderId}`);
        onPaid?.();
      } else if (confirm.status === "PAYER_ACTION_REQUIRED") {
        await paypal.Googlepay().initiatePayerAction({ orderId });
        // After 3DS, capture
        await axios.post(`${API}/paypal/capture/${orderId}`);
        onPaid?.();
      } else {
        throw new Error(`Google Pay confirmOrder status: ${confirm.status}`);
      }
    } catch (err) {
      // Google Pay cancellations throw with statusCode "CANCELED" — silent skip
      if (err && err.statusCode === "CANCELED") return;
      // eslint-disable-next-line no-console
      console.error("[GooglePay] payment error", err);
      onError?.(err);
    }
  };

  if (!eligible) return null;

  return <div ref={containerRef} className="w-full" data-testid="google-pay-container" />;
};

export default GooglePayButton;
